import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = 'https://groovie-business-api-production.up.railway.app/api/v1';

// Genre options are sourced live from GET /api/v1/genres/catalog (backed by genre_map) — no hardcoded
// list, so the picker can never drift from what the catalog can actually fill.
const MOOD_OPTIONS = ['energetic', 'chill', 'uplifting', 'relaxed', 'focused', 'romantic', 'peaceful', 'fun'];

// Fix 4: friendly stages mapped to the REAL provision_steps the tracked setup writes. No fake progress —
// a stage lights up only when a real step under it has completed.
const PROVISION_STAGES = [
  { label: 'Setting up your station', steps: ['r2_storage', 'assign_storage'] },
  { label: 'Building your playlist', steps: ['main_rotation_create', 'main_rotation_pool', 'main_rotation_allmedia', 'station_ids_create', 'station_ids_files', 'dj_drops_create', 'dj_drops_files'] },
  { label: 'Outlining your segments', steps: ['precreate_announcements', 'precreate_ad', 'precreate_urgent'] },
  { label: 'Tuning the sound & going live', steps: ['webhook', 'audio_normalization', 'start'] },
];
// Highest stage index that has at least one completed (ok) real step -> the active stage.
function activeProvisionStage(steps) {
  const done = new Set((steps || []).filter(s => s && s.ok).map(s => s.step));
  let idx = -1;
  PROVISION_STAGES.forEach((st, i) => { if (st.steps.some(k => done.has(k))) idx = i; });
  return idx;
}

const DEFAULT_CHANNEL = {
  name: '', description: '', location_id: '',
  preferred_genres: [], preferred_moods: [], preferred_tempo: 'moderate',
  instrumental_only: false,   // vocal-tiers: background music, no vocals — a filter on top of the picked genres
  explicit_content: false, is_public: false
};

const SvgIcon = ({ type, size = 16 }) => {
  const icons = {
    play: <polygon points="5,3 19,12 5,21" fill="currentColor" />,
    stop: <rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" />,
    edit: <><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></>,
    trash: <><polyline points="3,6 5,6 21,6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></>,
    globe: <><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></>,
    lock: <><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></>,
    pin: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></>,
    music: <><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></>,
    calendar: <><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    radio: <><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.4"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.4"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/></>,
    plus: <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>,
    arrow: <><polyline points="9,18 15,12 9,6"/></>,
    clock: <><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[type]}</svg>
  );
};

export default function Channels() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [channels, setChannels] = useState([]);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = 'success') => { setToast({ msg, type }); setTimeout(() => setToast(null), 5000); };
  const [locations, setLocations] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ ...DEFAULT_CHANNEL });
  const [saving, setSaving] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [streamHealth, setStreamHealth] = useState({});
  // Fix 4: live provisioning progress for a freshly-created channel (polls the REAL provision_steps).
  const [provisioning, setProvisioning] = useState(null); // { channelId, name, status, steps, complete, startedAt }
  // Genre availability keyed by genre -> {state: healthy|warn|disabled|hidden, count}.
  // Honest pruning: the catalog can't back every offered genre, so we only let businesses
  // pick what actually exists at rotation depth.
  const [genreCatalog, setGenreCatalog] = useState({});
  const audioRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (!token) { navigate('/login'); return; }
    fetchAll();
    fetch(`${API}/genres/catalog`)
      .then(r => r.json())
      .then(d => setGenreCatalog(Object.fromEntries((d.genres || []).map(g => [g.genre, g]))))
      .catch(() => setGenreCatalog({}));
  }, [token, navigate]);

  // Fix 4 + (B): poll the REAL provision status/steps every 4s while a channel is provisioning.
  // Stops on complete (station is truly live) OR failed (provisioning couldn't bring it live — we've been alerted).
  useEffect(() => {
    if (!provisioning || provisioning.complete || provisioning.failed) return;
    let alive = true;
    const tick = async () => {
      try {
        const res = await fetch(`${API}/channels/${provisioning.channelId}/provision-status`, { headers });
        if (!res.ok) return;
        const d = await res.json();
        if (!alive) return;
        setProvisioning(p => p && ({ ...p, status: d.provision_status, steps: d.steps || [], complete: !!d.complete, failed: !!d.failed }));
        if (d.complete) { fetchChannels(); }
      } catch { /* transient — keep polling; the tracked job retries idempotently */ }
    };
    tick();
    const id = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(id); };
  }, [provisioning?.channelId, provisioning?.complete, provisioning?.failed]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const locId = searchParams.get('location');
    if (locId && locations.length > 0 && !loading) {
      setFormData({ ...DEFAULT_CHANNEL, location_id: locId });
      setEditing(false);
      setShowModal(true);
    }
  }, [searchParams, locations, loading]);

  const headers = { 'Authorization': `Bearer ${token}` };
  const jsonHeaders = { ...headers, 'Content-Type': 'application/json' };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchChannels(), fetchLocations(), fetchPrograms()]);
    setLoading(false);
  };

  const fetchChannels = async () => {
    try {
      const res = await fetch(`${API}/channels`, { headers });
      if (res.status === 401) { navigate('/login'); return; }
      if (res.ok) { const data = await res.json(); setChannels(data.channels || []); }
    } catch (err) { console.error(err); setChannels([]); }
  };

  const fetchLocations = async () => {
    try {
      const res = await fetch(`${API}/locations`, { headers });
      if (res.ok) { const data = await res.json(); setLocations(data.locations || []); }
    } catch (err) { console.error(err); }
  };

  const fetchPrograms = async () => {
    try {
      const res = await fetch(`${API}/radio-programs`, { headers });
      if (res.ok) { const data = await res.json(); setPrograms(data.programs || []); }
    } catch (err) { console.error(err); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...formData, location_id: formData.location_id ? parseInt(formData.location_id) : null };
      const url = editing ? `${API}/channels/${editingId}` : `${API}/channels`;
      const res = await fetch(url, {
        method: editing ? 'PUT' : 'POST', headers: jsonHeaders, body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      await fetchAll();
      closeModal();
      // Honest signal: backend flags any picked genres with no catalog yet (curated-default substitution).
      const gn = data.genre_notice;
      if (gn && gn.empty_genres && gn.empty_genres.length) {
        const names = gn.empty_genres.map(g => `'${String(g).replace('_', ' ')}'`);
        const list = names.length === 1 ? names[0]
          : names.slice(0, -1).join(', ') + ' and ' + names.slice(-1);
        const verb = names.length === 1 ? "isn't" : "aren't";
        setTimeout(() => showToast(`${list} ${verb} in our catalog yet — your station is playing a curated palette until it is.`, 'warn'), 600);
      }
      if (!editing) {
        const chId = data?.channel?.id;
        if (chId) {
          setProvisioning({ channelId: chId, name: formData.name || 'Your station', status: 'provisioning', steps: [], complete: false, startedAt: Date.now() });
        } else {
          showToast('Channel created! Your station is being set up.', 'success');
        }
      } else {
        showToast('Channel updated successfully.', 'success');
      }
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete channel "${name}"? This will also remove its streaming station.`)) return;
    try {
      const res = await fetch(`${API}/channels/${id}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error('Failed');
      if (playingId === id) stopPlayback();
      await fetchAll();
    } catch (err) { alert('Failed to delete'); }
  };

  const togglePublic = async (id) => {
    try {
      const res = await fetch(`${API}/channels/${id}/toggle-public`, { method: 'PUT', headers });
      if (res.ok) await fetchChannels();
    } catch (err) { console.error(err); }
  };

  // ---- 2.5.2 Format Clock (per-channel programming template + Groovie ident tiering) ----
  const [fc, setFc] = useState(null);  // { channelId, name, data:{...cadence}, policy, planTier, saving }
  const IDENT_LABEL = { always: 'On', hourly: 'Hourly (co-branding)', none: 'Off (white-label)' };

  const openFormatClock = async (ch) => {
    setFc({ channelId: ch.id, name: ch.name, loading: true,
            data: { station_id_every_songs: 5, jingle_every_min: 15, ad_slots_per_hour: 0, announcement_slots_per_hour: 0 },
            policy: 'always', planTier: '' });
    try {
      const res = await fetch(`${API}/channels/${ch.id}/format-clock`, { headers });
      if (res.ok) {
        const d = await res.json();
        const c = d.clock || {};
        setFc(prev => ({ ...prev, loading: false, policy: d.derived_ident_policy || 'always', planTier: d.plan_tier || '',
          data: {
            station_id_every_songs: c.station_id_every_songs ?? 5,
            jingle_every_min: c.jingle_every_min ?? 15,
            ad_slots_per_hour: c.ad_slots_per_hour ?? 0,
            announcement_slots_per_hour: c.announcement_slots_per_hour ?? 0,
          } }));
      } else { setFc(prev => ({ ...prev, loading: false })); }
    } catch (err) { setFc(prev => prev && ({ ...prev, loading: false })); }
  };

  const saveFormatClock = async () => {
    if (!fc) return;
    setFc(prev => ({ ...prev, saving: true }));
    try {
      const res = await fetch(`${API}/channels/${fc.channelId}/format-clock`, {
        method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ name: 'Default Clock', ...fc.data }),
      });
      if (!res.ok) throw new Error('Failed');
      const d = await res.json();
      showToast(`Format clock applied (approximately). Groovie idents: ${IDENT_LABEL[d.groovie_ident_policy] || d.groovie_ident_policy}.`, 'success');
      setFc(null);
    } catch (err) { showToast('Failed to apply clock: ' + err.message, 'error'); setFc(prev => ({ ...prev, saving: false })); }
  };

  const openEdit = (ch) => {
    setFormData({
      name: ch.name || '', description: ch.description || '',
      location_id: ch.location_id ? String(ch.location_id) : '',
      preferred_genres: ch.preferred_genres || [], preferred_moods: ch.preferred_moods || [],
      preferred_tempo: ch.preferred_tempo || 'moderate',
      instrumental_only: ch.instrumental_only || false,
      explicit_content: ch.explicit_content || false, is_public: ch.is_public || false
    });
    setEditing(true); setEditingId(ch.id); setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); setEditing(false); setEditingId(null); setFormData({ ...DEFAULT_CHANNEL }); };

  const toggleGenre = (genre) => {
    setFormData(prev => ({
      ...prev,
      preferred_genres: prev.preferred_genres.includes(genre)
        ? prev.preferred_genres.filter(g => g !== genre) : [...prev.preferred_genres, genre]
    }));
  };

  const toggleMood = (mood) => {
    setFormData(prev => ({
      ...prev,
      preferred_moods: prev.preferred_moods.includes(mood)
        ? prev.preferred_moods.filter(m => m !== mood) : [...prev.preferred_moods, mood]
    }));
  };

  const playStream = (channel) => {
    if (playingId === channel.id) { stopPlayback(); return; }
    stopPlayback();
    if (!channel.stream_url) { alert('Stream is still being set up. This usually takes a few minutes.'); return; }
    const audio = new Audio(channel.stream_url);
    audio.crossOrigin = 'anonymous';
    audioRef.current = audio;
    setPlayingId(channel.id);
    audio.play().catch(err => { console.error('Playback error:', err); setPlayingId(null); setStreamHealth(prev => ({ ...prev, [channel.id]: 'error' })); });
    audio.onplaying = () => setStreamHealth(prev => ({ ...prev, [channel.id]: 'live' }));
    audio.onerror = () => { setPlayingId(null); setStreamHealth(prev => ({ ...prev, [channel.id]: 'error' })); };
  };

  const stopPlayback = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; audioRef.current = null; }
    setPlayingId(null);
  };

  useEffect(() => { return () => stopPlayback(); }, []);

  const getLocationName = (locId) => locations.find(l => l.id === locId)?.name;
  const getProgramCount = (chId) => programs.filter(p => p.channel_id === chId).length;

  if (loading) {
    return (
      <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid #e9d5ff', borderTopColor: '#7c3aed', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 40px', maxWidth: 1200, margin: '0 auto', fontFamily: "'Outfit', 'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#111827', margin: 0, letterSpacing: '-0.5px' }}>Channels</h1>
          <p style={{ fontSize: 14, color: '#9ca3af', marginTop: 4 }}>{channels.length} channel{channels.length !== 1 ? 's' : ''} configured</p>
        </div>
        <button onClick={() => { setFormData({ ...DEFAULT_CHANNEL }); setEditing(false); setShowModal(true); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
            color: '#fff', border: 'none', borderRadius: 12,
            padding: '12px 24px', fontSize: 14, fontWeight: 600,
            cursor: 'pointer', fontFamily: "'Outfit', sans-serif",
            boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(124,58,237,0.4)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,0.3)'; }}
        >
          <SvgIcon type="plus" size={18} /> New Channel
        </button>
      </div>

      {/* Empty State */}
      {channels.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '64px 24px',
          background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
        }}>
          <div style={{ color: '#9ca3af', marginBottom: 16 }}><SvgIcon type="radio" size={48} /></div>
          <h3 style={{ fontSize: 20, fontWeight: 700, color: '#374151', margin: '0 0 8px' }}>No channels yet</h3>
          <p style={{ fontSize: 14, color: '#9ca3af', margin: '0 0 24px' }}>Create a channel to start broadcasting to your locations.</p>
          <button onClick={() => { setFormData({ ...DEFAULT_CHANNEL }); setEditing(false); setShowModal(true); }}
            style={{
              background: 'linear-gradient(135deg, #7c3aed, #a855f7)', color: '#fff',
              border: 'none', borderRadius: 10, padding: '12px 28px',
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>Create Your First Channel</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
          {channels.map(ch => {
            const locationName = getLocationName(ch.location_id);
            const progCount = getProgramCount(ch.id);
            const isPlaying = playingId === ch.id;
            const health = streamHealth[ch.id];
            // (B) never a dead Play: only playable once truly provisioned (not failed, not still provisioning).
            const playable = !!ch.stream_url && ch.provision_status !== 'failed' && ch.provision_status !== 'provisioning';

            return (
              <div key={ch.id} style={{
                background: '#fff', borderRadius: 16, border: '1px solid #e5e7eb',
                overflow: 'hidden', transition: 'transform 0.15s, box-shadow 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.06)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ padding: '20px 22px' }}>
                  {/* Location breadcrumb */}
                  {locationName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#9ca3af', marginBottom: 8 }}>
                      <SvgIcon type="pin" size={12} />
                      <button onClick={() => navigate('/locations')}
                        style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: 11, padding: 0 }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                      >{locationName}</button>
                    </div>
                  )}

                  {/* Channel name + badges */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                    <h3 style={{ fontSize: 16, fontWeight: 700, color: '#111827', margin: 0 }}>{ch.name}</h3>
                    {ch.is_public && (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20,
                        fontSize: 11, fontWeight: 600,
                        background: '#ecfdf5', color: '#059669',
                      }}>
                        <SvgIcon type="globe" size={10} /> Public
                      </span>
                    )}
                    {ch.provision_status === 'failed' ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: '#fef2f2', color: '#dc2626',
                      }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#dc2626' }} />
                        Setup problem
                      </span>
                    ) : ch.provision_status === 'provisioning' ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                        background: '#eff6ff', color: '#2563eb',
                      }}>Setting up…</span>
                    ) : ch.azuracast_station_id ? (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20,
                        fontSize: 11, fontWeight: 600,
                        background: health === 'live' ? '#ecfdf5' : health === 'error' ? '#fef2f2' : '#f0fdf4',
                        color: health === 'live' ? '#059669' : health === 'error' ? '#dc2626' : '#16a34a',
                      }}>
                        <span style={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: health === 'live' ? '#059669' : health === 'error' ? '#dc2626' : '#16a34a',
                          ...(health === 'live' ? { animation: 'pulse 2s infinite' } : {}),
                        }} />
                        {health === 'live' ? 'Live' : health === 'error' ? 'Reconnecting' : 'Ready'}
                      </span>
                    ) : (
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        padding: '2px 10px', borderRadius: 20,
                        fontSize: 11, fontWeight: 600,
                        background: '#eff6ff', color: '#2563eb',
                      }}>Setting up stream...</span>
                    )}
                  </div>

                  {ch.description && <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 10px' }}>{ch.description}</p>}

                  {/* Meta info */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 12, color: '#9ca3af' }}>
                    {!locationName && <span style={{ color: '#d97706' }}>No location assigned</span>}
                    {ch.preferred_genres?.length > 0 && (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <SvgIcon type="music" size={12} /> {ch.preferred_genres.slice(0, 3).join(', ')}
                        {ch.instrumental_only && <span style={{ color: '#6b7280' }}> · instrumental</span>}
                      </span>
                    )}
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <SvgIcon type="calendar" size={12} /> {progCount} program{progCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {/* Visible fallback (never silent): the owner asked for instrumental-only but the picked
                      genre has no instrumental pool yet, so the station is playing the full selection. */}
                  {ch.instrumental_only && ch.instrumental_fallback && (
                    <div style={{ marginTop: 8, fontSize: 11.5, color: '#b45309', background: '#fffbeb',
                                  border: '1px solid #fde68a', borderRadius: 8, padding: '7px 10px' }}>
                      Instrumental-only isn't available for this genre yet — your station is playing the full selection.
                    </div>
                  )}
                </div>

                {/* Actions bar */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '12px 22px',
                  borderTop: '1px solid #f3f4f6',
                  background: '#fafafa',
                }}>
                  {playable ? (
                    <button onClick={() => playStream(ch)} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: 'none', cursor: 'pointer', transition: 'all 0.15s',
                      background: isPlaying ? '#fef2f2' : '#f5f3ff',
                      color: isPlaying ? '#dc2626' : '#7c3aed',
                    }}>
                      <SvgIcon type={isPlaying ? 'stop' : 'play'} size={12} />
                      {isPlaying ? 'Stop' : 'Play'}
                    </button>
                  ) : ch.provision_status === 'failed' ? (
                    <button disabled title="Setup problem — our team has been notified" style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                      border: 'none', cursor: 'not-allowed', background: '#f3f4f6', color: '#9ca3af',
                    }}>
                      <SvgIcon type="play" size={12} /> Unavailable
                    </button>
                  ) : null}
                  <button onClick={() => navigate(`/radio-programs?channel=${ch.id}`)} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: 'none', cursor: 'pointer', background: '#eff6ff', color: '#2563eb',
                  }}>
                    <SvgIcon type="calendar" size={12} /> Schedule
                  </button>
                  <button onClick={() => togglePublic(ch.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: 'none', cursor: 'pointer', background: '#f3f4f6', color: '#4b5563',
                  }}>
                    <SvgIcon type={ch.is_public ? 'lock' : 'globe'} size={12} />
                    {ch.is_public ? 'Make Private' : 'Make Public'}
                  </button>
                  <div style={{ flex: 1 }} />
                  <button onClick={() => openFormatClock(ch)} title="Format Clock" style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '7px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                    border: 'none', cursor: 'pointer', background: '#f3f4f6', color: '#4b5563',
                  }}>
                    <SvgIcon type="clock" size={12} /> Format Clock
                  </button>
                  <button onClick={() => openEdit(ch)} style={{
                    padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: 'transparent', color: '#9ca3af', transition: 'color 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
                  ><SvgIcon type="edit" size={14} /></button>
                  <button onClick={() => handleDelete(ch.id, ch.name)} style={{
                    padding: 7, borderRadius: 8, border: 'none', cursor: 'pointer',
                    background: 'transparent', color: '#d1d5db', transition: 'color 0.15s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#d1d5db'}
                  ><SvgIcon type="trash" size={14} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 2.5.2 FORMAT CLOCK MODAL */}
      {fc && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:50, padding:16, backdropFilter:'blur(4px)' }}>
          <div style={{ background:'#fff', borderRadius:20, width:'100%', maxWidth:520, maxHeight:'90vh', overflowY:'auto', padding:32, boxShadow:'0 25px 60px rgba(0,0,0,0.15)' }}>
            <h2 style={{ fontSize:22, fontWeight:700, color:'#111827', margin:'0 0 4px', letterSpacing:'-0.3px' }}>Format Clock</h2>
            <p style={{ fontSize:13, color:'#6b7280', margin:'0 0 20px' }}>{fc.name} &mdash; how your hour is programmed. The station follows this approximately (best-effort scheduling).</p>

            {[
              { k:'station_id_every_songs', label:'Station ID every N songs', min:1, max:20 },
              { k:'jingle_every_min', label:'Jingle / DJ drop every N minutes', min:1, max:60 },
              { k:'ad_slots_per_hour', label:'Ad slots per hour', min:0, max:12 },
              { k:'announcement_slots_per_hour', label:'Announcement slots per hour', min:0, max:12 },
            ].map(f => (
              <div key={f.k} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
                <label style={{ fontSize:13, fontWeight:600, color:'#374151' }}>{f.label}</label>
                <input type="number" min={f.min} max={f.max} value={fc.data[f.k]}
                  onChange={(e)=>setFc(prev=>({ ...prev, data:{ ...prev.data, [f.k]: Math.max(f.min, Math.min(f.max, parseInt(e.target.value)||0)) } }))}
                  style={{ width:80, padding:'8px 10px', borderRadius:8, border:'1px solid #e5e7eb', fontSize:14, textAlign:'center' }} />
              </div>
            ))}

            <div style={{ background:'#f9fafb', borderRadius:12, padding:14, margin:'8px 0 18px' }}>
              <div style={{ fontSize:11, fontWeight:700, color:'#6b7280', textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:8 }}>Example hour (approximately)</div>
              <div style={{ fontSize:13, color:'#374151', lineHeight:1.7 }}>
                Music rotation, with a station ID about every {fc.data.station_id_every_songs} song{fc.data.station_id_every_songs!==1?'s':''} and a jingle about every {fc.data.jingle_every_min} min
                {fc.data.ad_slots_per_hour>0 ? `, ${fc.data.ad_slots_per_hour} ad slot(s)/hr` : ''}
                {fc.data.announcement_slots_per_hour>0 ? `, ${fc.data.announcement_slots_per_hour} announcement slot(s)/hr` : ''}.
              </div>
            </div>

            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', background:'#f3f4ff', borderRadius:12, padding:'12px 14px', marginBottom:20 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:'#374151' }}>Groovie idents: {IDENT_LABEL[fc.policy] || fc.policy}</div>
                <div style={{ fontSize:12, color:'#6b7280', marginTop:2 }}>
                  {fc.policy==='none' ? 'White-label: only your own uploaded idents air.' : fc.policy==='hourly' ? 'Groovie idents air about once an hour.' : 'Groovie idents air at your Station ID cadence.'}
                </div>
              </div>
              {fc.policy!=='none' && (
                <button onClick={()=>navigate('/billing')} style={{ fontSize:12, fontWeight:600, color:'#7c3aed', background:'transparent', border:'none', cursor:'pointer', whiteSpace:'nowrap' }}>Manage</button>
              )}
            </div>

            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={()=>setFc(null)} style={{ padding:'10px 18px', borderRadius:10, border:'1px solid #e5e7eb', background:'#fff', color:'#374151', fontWeight:600, fontSize:14, cursor:'pointer' }}>Cancel</button>
              <button onClick={saveFormatClock} disabled={fc.saving||fc.loading} style={{ padding:'10px 18px', borderRadius:10, border:'none', background:(fc.saving||fc.loading)?'#c4b5fd':'#7c3aed', color:'#fff', fontWeight:600, fontSize:14, cursor:(fc.saving||fc.loading)?'not-allowed':'pointer' }}>
                {fc.saving ? 'Applying...' : 'Apply clock'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pulse animation */}
      <style>{`@keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }`}</style>

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 50, padding: 16, backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: '#fff', borderRadius: 20, width: '100%', maxWidth: 520,
            maxHeight: '90vh', overflowY: 'auto', padding: 32,
            boxShadow: '0 25px 60px rgba(0,0,0,0.15)',
          }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 24px', letterSpacing: '-0.3px' }}>
              {editing ? 'Edit Channel' : 'New Channel'}
            </h2>
            <form onSubmit={handleSave}>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Channel Name *</label>
                <input type="text" required value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Main Floor Music"
                  style={{
                    width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
                    padding: '11px 14px', fontSize: 14, outline: 'none',
                    fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Description</label>
                <input type="text" value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="What plays on this channel?"
                  style={{
                    width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
                    padding: '11px 14px', fontSize: 14, outline: 'none',
                    fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
                    transition: 'border-color 0.15s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#7c3aed'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Location *</label>
                <select value={formData.location_id}
                  onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                  style={{
                    width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
                    padding: '11px 14px', fontSize: 14, outline: 'none',
                    fontFamily: "'Outfit', sans-serif", boxSizing: 'border-box',
                    background: '#fff',
                  }}>
                  <option value="">Select a location...</option>
                  {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  Genres {formData.preferred_genres.length > 0 && `(${formData.preferred_genres.length})`}
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {Object.values(genreCatalog)
                    .filter(info => info && info.state !== 'hidden')            // catalog is the SOLE source — no hardcoded list, so the picker can never drift
                    .sort((a, b) => (b.count || 0) - (a.count || 0))           // richest rotations first
                    .map(info => {
                    const g = info.genre;
                    const selected = formData.preferred_genres.includes(g);
                    const disabled = info.state === 'disabled';                  // below floor -> can't pick yet
                    const warn = info.state === 'warn';                          // thin but usable
                    // Instrumental-only mode: a genre with no instrumental pool stays pickable but will FALL
                    // BACK to the full selection (visible, not silent) — flag it so the choice is informed.
                    const instrOn = formData.instrumental_only;
                    const noInstr = instrOn && !info.instrumental_selectable;
                    const title = disabled ? 'Coming as our catalog grows'
                      : instrOn ? `${info.instrumental_count} instrumental of ${info.count} tracks`
                          + (noInstr ? ' — no instrumental pool yet, so picking this plays the full selection' : '')
                      : warn ? `Thin rotation (${info.count} tracks)` : `${info.count} tracks`;
                    return (
                      <button key={g} type="button"
                        onClick={() => !disabled && toggleGenre(g)}
                        disabled={disabled}
                        title={title}
                        style={{
                          padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          border: (warn || noInstr) && selected ? '1px solid #d97706' : 'none',
                          cursor: disabled ? 'not-allowed' : 'pointer', textTransform: 'capitalize',
                          transition: 'all 0.15s', opacity: disabled ? 0.45 : 1,
                          background: selected ? '#7c3aed' : '#f3f4f6',
                          color: selected ? '#fff' : disabled ? '#9ca3af' : '#6b7280',
                        }}>
                        {g.replace('_', ' ')}
                        {disabled && ' · soon'}
                        {warn && !disabled && ' ·⚠'}
                        {noInstr && !disabled && ' · vocals'}
                      </button>
                    );
                  })}
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9ca3af' }}>
                  Only genres our catalog can fill are selectable. "⚠" = thinner rotation; "soon" = coming as the catalog grows.
                </p>
              </div>

              {/* Retired genres: a stored pick our catalogue no longer offers (e.g. rock) — surface it so the
                  owner can remove it. It otherwise silently resolves to the default background palette. */}
              {formData.preferred_genres.filter(g => !genreCatalog[g]).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <p style={{ margin: '0 0 6px', fontSize: 11, color: '#9ca3af' }}>No longer offered — click to remove:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {formData.preferred_genres.filter(g => !genreCatalog[g]).map(g => (
                      <button key={g} type="button" onClick={() => toggleGenre(g)}
                        title="This genre is no longer in our catalogue — click to remove"
                        style={{ padding: '6px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                                 border: '1px dashed #d1d5db', background: '#fff', color: '#9ca3af',
                                 cursor: 'pointer', textTransform: 'capitalize' }}>
                        {g.replace('_', ' ')} · retired ×
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Instrumental-only (vocal-tiers): a filter on top of the picked genres — background, not foreground.
                  Available on every genre; a genre with no instrumental pool falls back to the full selection
                  (flagged "· vocals" above, and reported on the channel card when it happens). */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!formData.instrumental_only}
                    onChange={e => setFormData({ ...formData, instrumental_only: e.target.checked })}
                    style={{ width: 16, height: 16, cursor: 'pointer' }} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Instrumental only</span>
                  <span style={{ fontSize: 12, color: '#9ca3af' }}>— background music, no vocals</span>
                </label>
                <p style={{ margin: '6px 0 0 26px', fontSize: 11, color: '#9ca3af' }}>
                  Available on every genre. A "· vocals" genre has no instrumental pool yet, so picking it plays the full selection.{' '}
                  <button type="button"
                    onClick={() => setFormData({ ...formData, preferred_genres: ['jazz'], instrumental_only: true })}
                    style={{ border: 'none', background: 'none', padding: 0, color: '#7c3aed', fontWeight: 600,
                             fontSize: 11, cursor: 'pointer', textDecoration: 'underline' }}>
                    Use “Jazz — instrumental”
                  </button>
                </p>
              </div>

              <div style={{ marginBottom: 18 }}>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                  Moods <span style={{ fontWeight: 400, color: '#9ca3af' }}>— coming soon</span>
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {MOOD_OPTIONS.map(m => (
                    <button key={m} type="button" disabled title="Mood filtering isn't available yet"
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        border: 'none', cursor: 'not-allowed', textTransform: 'capitalize',
                        opacity: 0.45, background: '#f3f4f6', color: '#9ca3af',
                      }}>{m}</button>
                  ))}
                </div>
                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#9ca3af' }}>
                  Mood-based selection isn't wired yet — our catalog has no mood data. Coming later.
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginBottom: 24 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    style={{ width: 16, height: 16, accentColor: '#7c3aed' }} />
                  Public on groovie.live
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#374151' }}>
                  <input type="checkbox" checked={formData.explicit_content}
                    onChange={(e) => setFormData({ ...formData, explicit_content: e.target.checked })}
                    style={{ width: 16, height: 16, accentColor: '#7c3aed' }} />
                  Explicit content OK
                </label>
              </div>
              <div style={{ display: 'flex', gap: 12, borderTop: '1px solid #f3f4f6', paddingTop: 20 }}>
                <button type="button" onClick={closeModal}
                  style={{
                    flex: 1, padding: '12px 20px', borderRadius: 10,
                    border: '1.5px solid #e5e7eb', background: '#fff',
                    color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                  }}>Cancel</button>
                <button type="submit" disabled={saving}
                  style={{
                    flex: 1, padding: '12px 20px', borderRadius: 10,
                    border: 'none', background: saving ? '#c4b5fd' : 'linear-gradient(135deg, #7c3aed, #a855f7)',
                    color: '#fff', fontSize: 14, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                    fontFamily: "'Outfit', sans-serif",
                    boxShadow: '0 4px 12px rgba(124,58,237,0.3)',
                  }}>{saving ? 'Saving...' : (editing ? 'Save Changes' : 'Create Channel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {provisioning && (() => {
        const steps = provisioning.steps || [];
        const active = activeProvisionStage(steps);
        const done = provisioning.complete;
        const failed = provisioning.failed;
        const elapsed = (Date.now() - (provisioning.startedAt || Date.now())) / 1000;
        const slow = !done && elapsed > 300;
        const critical = ['r2_storage','main_rotation_create','main_rotation_pool','main_rotation_allmedia','precreate_announcements','precreate_ad','precreate_urgent','webhook','start'];
        const failedCritical = steps.find(s => s && !s.ok && critical.includes(s.step));
        const stuck = !done && (provisioning.status === 'deferred' || (slow && !!failedCritical));
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:60, padding:16, backdropFilter:'blur(4px)' }}>
            <div style={{ background:'#fff', borderRadius:16, padding:'32px 28px', width:'100%', maxWidth:440, boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>
              <h3 style={{ margin:'0 0 4px', fontSize:18, fontWeight:700, color: failed ? '#b91c1c' : '#111827' }}>
                {failed ? `${provisioning.name} — setup problem` : done ? `${provisioning.name} is live!` : `Setting up ${provisioning.name}`}
              </h3>
              <p style={{ margin:'0 0 20px', fontSize:13, color: failed ? '#b91c1c' : stuck ? '#b45309' : '#6b7280' }}>
                {failed ? "We couldn't finish setting up your station, and our team has been automatically notified. We'll email you the moment it's fixed — nothing you need to do."
                  : done ? 'Your station is ready to stream.'
                  : stuck ? 'Something needs attention — our system is retrying automatically. This usually resolves on its own.'
                  : slow ? 'This is taking a little longer than usual — our system is still working. Large libraries can take a few minutes.'
                  : 'This may take a few minutes. You can keep this open or continue in the background.'}
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:22 }}>
                {PROVISION_STAGES.map((st, i) => {
                  const stageDone = done || i < active;
                  const stageActive = !done && !failed && i === (active < 0 ? 0 : active);
                  return (
                    <div key={i} style={{ display:'flex', alignItems:'center', gap:10, opacity: (stageDone || stageActive) ? 1 : 0.4 }}>
                      <span style={{ width:20, height:20, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700,
                        background: stageDone ? '#7c3aed' : stageActive ? '#ede9fe' : '#f3f4f6', color: stageDone ? '#fff' : stageActive ? '#7c3aed' : '#9ca3af' }}>
                        {stageDone ? '✓' : stageActive ? '•' : ''}
                      </span>
                      <span style={{ fontSize:14, fontWeight: stageActive ? 700 : 500, color: (stageDone || stageActive) ? '#111827' : '#9ca3af' }}>
                        {st.label}{stageActive && !stuck ? '…' : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              {failed ? (
                <button onClick={() => setProvisioning(null)} style={{ width:'100%', padding:12, borderRadius:10, border:'none', background:'#111827', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer' }}>Close</button>
              ) : done ? (
                <button onClick={() => setProvisioning(null)} style={{ width:'100%', padding:12, borderRadius:10, border:'none', background:'linear-gradient(135deg,#7c3aed,#6d28d9)', color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer' }}>View my station</button>
              ) : (
                <button onClick={() => setProvisioning(null)} style={{ width:'100%', padding:11, borderRadius:10, border:'1.5px solid #e5e7eb', background:'#fff', color:'#6b7280', fontWeight:600, fontSize:13, cursor:'pointer' }}>Continue in the background</button>
              )}
            </div>
          </div>
        );
      })()}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
          background: toast.type === 'error' ? '#ef4444' : toast.type === 'warn' ? '#d97706' : '#7c3aed',
          color: '#fff', padding: '14px 28px', borderRadius: 12,
          fontSize: 14, fontWeight: 500, zIndex: 9999, maxWidth: 460,
          boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 10
        }}>
          <span>{toast.type === 'error' ? '✕' : toast.type === 'warn' ? '⚠' : '✓'}</span>
          {toast.msg}
        </div>
      )}
    </div>
  );
}